import { useEffect, useState } from 'react';
import { supabase, BlogPost } from '@/lib/supabase';
import { Link } from 'react-router-dom';
import { PlusCircle, Edit, Eye, Trash2, BarChart3, ArrowLeft, Star, Loader2 } from 'lucide-react';

export function AdminBlogList() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  async function fetchPosts() {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*, categories:blog_categories(name)')
      .order('created_at', { ascending: false });
    
    if (!error && data) setPosts(data);
    setLoading(false);
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres borrar este artículo? Esta acción no se puede deshacer.')) return;
    await supabase.from('blog_posts').delete().eq('id', id);
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  const toggleFeatured = async (post: BlogPost) => {
    setTogglingId(post.id);
    try {
        const newValue = !post.is_featured;
        const { error } = await supabase
            .from('blog_posts')
            .update({ is_featured: newValue })
            .eq('id', post.id);

        if (error) throw error;

        // Actualizar estado local
        setPosts(prev => prev.map(p => 
            p.id === post.id ? { ...p, is_featured: newValue } : p
        ));
    } catch (error) {
        console.error('Error updating featured status:', error);
        alert('No se pudo actualizar el estado destacado.');
    } finally {
        setTogglingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-full text-slate-500">
                <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Gestión del Blog</h1>
                <p className="text-slate-500">Gestiona tus artículos y decide cuáles destacar en la Home.</p>
            </div>
        </div>
        <Link 
          to="/admin/blog/new" 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
        >
          <PlusCircle className="w-5 h-5" /> Nuevo Artículo
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 w-16 text-center" title="Destacado en Home">Dest.</th>
              <th className="px-6 py-4">Título</th>
              <th className="px-6 py-4">Estado</th>
              <th className="px-6 py-4">Categoría</th>
              <th className="px-6 py-4 text-center">Visitas</th>
              <th className="px-6 py-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {posts.length === 0 ? (
                <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                        No hay artículos todavía. ¡Crea el primero!
                    </td>
                </tr>
            ) : posts.map(post => (
              <tr key={post.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-center">
                    <button 
                        onClick={() => toggleFeatured(post)}
                        disabled={togglingId === post.id}
                        className={`p-2 rounded-full transition-colors ${
                            post.is_featured 
                                ? 'text-yellow-400 hover:bg-yellow-50' 
                                : 'text-slate-300 hover:text-yellow-400 hover:bg-slate-100'
                        }`}
                        title={post.is_featured ? "Quitar de destacados" : "Destacar en Home"}
                    >
                        {togglingId === post.id ? (
                            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        ) : (
                            <Star className={`w-5 h-5 ${post.is_featured ? 'fill-yellow-400' : ''}`} />
                        )}
                    </button>
                </td>
                <td className="px-6 py-4 font-medium text-slate-900">
                  {post.title}
                  <div className="text-xs text-slate-400 font-normal mt-1 truncate max-w-xs">{post.slug}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {post.status === 'published' ? 'Publicado' : 'Borrador'}
                  </span>
                </td>
                <td className="px-6 py-4 text-slate-600">
                  {post.categories?.name || '-'}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1 text-slate-600">
                    <BarChart3 className="w-4 h-4" /> {post.view_count}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/blog/${post.slug}`} target="_blank" className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Ver en web">
                      <Eye className="w-4 h-4" />
                    </Link>
                    <Link to={`/admin/blog/edit/${post.id}`} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title="Editar">
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button onClick={() => handleDelete(post.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Borrar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
